import { Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material'
import { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { search } from 'store/person/index'

const LOAD_MORE = '__LOAD_MORE__'
const LIMIT = 15

export default function PersonSearch({ value, onChange, label, disabled, error, helperText }) {
  const dispatch = useDispatch()

  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [que, setQue] = useState('')
  const [total, setTotal] = useState(0)
  const debounceRef = useRef(null)
  const keepOpenRef = useRef(false)

  const fetchOptions = async (query, replace) => {
    setLoading(true)
    const params = { limit: LIMIT, offset: replace ? 0 : options.length }
    if (query) params.que = query
    const result = await dispatch(search(params))
    if (result.payload) {
      const rows = result.payload.rows || []
      if (replace) {
        setOptions(rows)
      } else {
        setOptions(prev => {
          const existingIds = new Set(prev.filter(p => p.id !== LOAD_MORE).map(p => p.id))
          return [...prev, ...rows.filter(r => !existingIds.has(r.id))]
        })
      }
      setTotal(result.payload.count || 0)
    }
    setLoading(false)
  }

  const handleOpen = () => {
    setOpen(true)
    if (options.length > 0) return
    fetchOptions(que, true)
  }

  const handleInputChange = (_, newInput, reason) => {
    if (reason !== 'input') return
    setQue(newInput)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchOptions(newInput, true), 300)
  }

  const hasMore = options.length < total
  const displayOptions = hasMore ? [...options, { id: LOAD_MORE }] : options

  return (
    <Autocomplete
      options={displayOptions}
      value={value}
      open={open}
      onOpen={handleOpen}
      onClose={() => { if (keepOpenRef.current) { keepOpenRef.current = false; return }; setOpen(false) }}
      loading={loading}
      disabled={disabled}
      filterOptions={x => x}
      getOptionLabel={option => option?.id === LOAD_MORE ? '' : `${option?.lastName || ''}, ${option?.firstName || ''}`}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      onInputChange={handleInputChange}
      onChange={(_, newValue) => {
        if (newValue?.id === LOAD_MORE) return
        onChange(newValue || null)
      }}
      renderOption={(props, option) => {
        if (option.id === LOAD_MORE) {
          return (
            <Box component="li" {...props} key="load-more" sx={{ justifyContent: 'center !important', p: '4px !important' }}>
              <Button
                size="small"
                variant="text"
                onMouseDown={e => {
                  e.preventDefault()
                  keepOpenRef.current = true
                  fetchOptions(que, false)
                }}
              >
                Load more ({total - options.length} remaining)
              </Button>
            </Box>
          )
        }
        return (
          <Box component="li" {...props} key={option.id}>
            {option.lastName}, {option.firstName}
          </Box>
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
